import { Trans, t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { Stack, Autocomplete, Paper, Box, Typography } from '@mui/material'
import { Dayjs } from 'dayjs'
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded'

import DatePickerInput from '@/components/ui/inputs/DatePickerInput'
import TextInput from '@/components/ui/inputs/TextInput'
import { TimeTrackingAdminStaff } from '@/core/api/time-tracking'

type AdminSessionsFilterProps = {
  from: Dayjs
  to: Dayjs
  staffs: TimeTrackingAdminStaff[]
  selectedStaffId: string | null
  onFromChange: (date: Dayjs) => void
  onToChange: (date: Dayjs) => void
  onStaffChange: (staffId: string | null) => void
}

export function AdminSessionsFilter({ from, to, staffs, selectedStaffId, onFromChange, onToChange, onStaffChange }: AdminSessionsFilterProps) {
  useLingui()

  const selectedStaff = staffs.find((s) => s._id === selectedStaffId) ?? null

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          px: 3,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'grey.100',
          bgcolor: 'grey.50',
        }}
      >
        <FilterAltRoundedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          <Trans>Filters</Trans>
        </Typography>
      </Stack>
      <Box sx={{ p: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'flex-end' }}>
          <Box sx={{ width: { xs: '100%', md: 200 } }}>
            <DatePickerInput label={t`From`} value={from} onChange={(date) => date && onFromChange(date)} fullWidth />
          </Box>
          <Box sx={{ width: { xs: '100%', md: 200 } }}>
            <DatePickerInput label={t`To`} value={to} onChange={(date) => date && onToChange(date)} fullWidth />
          </Box>
          <Box sx={{ width: { xs: '100%', md: 280 } }}>
            <Autocomplete<TimeTrackingAdminStaff>
              options={staffs}
              value={selectedStaff}
              onChange={(_, value) => onStaffChange(value?._id ?? null)}
              getOptionLabel={(staff) => `${staff.userInfo.name} ${staff.userInfo.surname}`}
              isOptionEqualToValue={(a, b) => a._id === b._id}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  padding: '0 !important',
                },
                '& .MuiAutocomplete-input': {
                  padding: '8px 12px !important',
                  fontSize: '14px',
                },
              }}
              renderInput={(params) => <TextInput {...params} label={t`Staff`} placeholder={t`All staff`} fullWidth />}
            />
          </Box>
        </Stack>
      </Box>
    </Paper>
  )
}
